import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable Select dropdown component for forms, integrated with react-hook-form.
 * Displays validation errors and supports customizable options.
 * Uses semantic color classes from shared configuration.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The field label text
 * @param {string} props.name - The field name (used for form registration and error display)
 * @param {Function} props.register - The react-hook-form register function
 * @param {Object} [props.rules] - Validation rules for react-hook-form
 * @param {Object} props.errors - The errors object from react-hook-form
 * @param {Array<{value: string|number, label: string}>} props.options - Array of options for the dropdown
 * @param {boolean} [props.required=false] - Whether the field is required (shows red asterisk)
 * @param {string} [props.placeholder] - Placeholder text for the select
 * @param {string} [props.helpText] - Help text displayed below the select
 * @param {string} [props.className] - Additional CSS classes
 */
const SelectField = ({ 
    label, 
    name, 
    register, 
    rules, 
    errors, 
    options, 
    required = false,
    placeholder,
    helpText,
    className = '',
    ...props 
}) => {
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    const hasError = errors && errors[name];
    const errorMessage = hasError ? errors[name].message : null;
    const placeholderText = placeholder || (label ? `Select ${label}...` : 'Select...');

    return (
        <div className={`mb-4 ${className}`}>
            {label && (
                <label htmlFor={name} className={`block text-sm font-medium ${semantic.formLabel} mb-1`}>
                    {replace(label)}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <select
                id={name}
                {...(register ? register(name, rules) : {})}
                className={`w-full px-3 py-2 ${semantic.formBg} ${semantic.formBorder} border rounded-md ${semantic.formText} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none ${
                    hasError ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                aria-invalid={hasError ? 'true' : 'false'}
                aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : null}
                {...props}
            >
                <option value="">{replace(placeholderText)}</option>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {replace(opt.label)}
                    </option>
                ))}
            </select>
            {helpText && !hasError && (
                <p id={`${name}-help`} className={`mt-1 text-xs ${semantic.formLabel}`}>
                    {replace(helpText)}
                </p>
            )}
            {hasError && (
                <p id={`${name}-error`} className="mt-1 text-xs text-red-500" role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

SelectField.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    register: PropTypes.func,
    rules: PropTypes.object,
    errors: PropTypes.object,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    className: PropTypes.string,
};

export default SelectField;

